import {
  AbstractType,
  Component,
  InjectionToken,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Type,
  ViewEncapsulation,
} from '@angular/core';
import { compact, get, isEmpty, isEqual, isFunction, map } from 'lodash';
import {
  GRID_ROW_SELECTION,
  IEventPickerOptions,
  IFieldPreferences,
  IModalDialogResult,
  IProcessedEvent,
  ISelectSiteAndEvents,
} from '@shared/types';
import { UiActions, XprtDataActions } from '@store/actions';

import { lazyInject } from '@app/containers/base/decorators';
import {
  STATE_SELECTORS,
  StateService,
  XprtDataService,
} from '@app/shared/services';
import { eventSelectorModalDefinition } from './modal.definition';
import { CANCEL_ACTION, MAX_PAGE_SIZE } from '@config/global-constants';
import { rx, Subject, Observable, Subscription } from '@app/shared/rx';
import { isSubject } from '@shared/utils';

// eslint-disable-line @angular-eslint/prefer-on-push-component-change-detection
// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xux-select-event-with-grid-modal',
  templateUrl: './xux-select-event-with-grid-modal-widget.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class XuxSelectEventWithGridModalComponent implements OnInit, OnDestroy {
  @lazyInject(StateService) public stateService: StateService;
  @lazyInject(XprtDataActions) public xprtDataActions: XprtDataActions;
  @lazyInject(XprtDataService) public xprtDataService: XprtDataService;

  @Input() public field: IFieldPreferences;

  public selectOptions$: Subject<any> = new Subject();

  private multiply: boolean = false;
  private maxItems: number;
  private minItems: number;
  private updateSelectOptionsWhenSingleSelected: boolean = false;
  private filterForPortalSelector: string[] = [];
  private sortForPortalSelector: string[] = [];
  private availableEventStatus: string[] = [];
  private subscriptions: Subscription[] = [];
  private disabled: boolean = false;
  private selectForEventPicker: string;
  private modalIsHidden$: Subject<boolean>;

  public constructor(
    public uiActions: UiActions,
    public injector: Injector,
  ) {}

  public ngOnInit(): void {
    this.multiply = get(this.field, 'widgetData.isMultiple', false);
    this.updateSelectOptionsWhenSingleSelected = get(
      this.field,
      'widgetData.updateSelectOptionsWhenSingleSelected',
      this.updateSelectOptionsWhenSingleSelected,
    );
    this.minItems = get(
      this.field,
      'widgetData.minItems',
      get(this.field, 'required') ? 1 : 0,
    );
    this.maxItems = get(this.field, 'widgetData.maxItems', MAX_PAGE_SIZE);
    this.filterForPortalSelector = get(
      this.field,
      'widgetData.filterForPortalSelector',
      [],
    );
    this.sortForPortalSelector = get(
      this.field,
      'widgetData.sortForPortalSelector',
      [],
    );
    this.availableEventStatus = get(
      this.field,
      'widgetData.availableEventStatus',
      [],
    );
    this.selectForEventPicker = get(this.field, 'widgetData.select', '');
    this.modalIsHidden$ = get(this.field, 'widgetData.modalIsHidden$', null);
    this.handleDisable();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub?.unsubscribe());
  }

  public handleDisable(): void {
    const disabledObs: Observable<boolean> = get(this, 'field.disabledObs');

    if (disabledObs) {
      this.subscriptions.push(
        disabledObs.subscribe((isDisabled: boolean) => {
          this.disabled = isDisabled;
        }),
      );
    }
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public async selectClickHandler(): Promise<void> {
    if (this.disabled) {
      return;
    }
    await this.openModal();
  }

  public async openModal(): Promise<void> {
    let result: IProcessedEvent[] | IModalDialogResult;

    if (this.stateService.getBySelector(STATE_SELECTORS.IFRAME_MODE)) {
      this.modalIsHidden$?.next(false);
      const gridExtraFilters: string[] = [];
      if (get(this.field, 'widgetData.useInputUrl')) {
        const parentValue: ISelectSiteAndEvents = get(
          this.field,
          'control.parent.value',
          '',
        );
        const url = get(parentValue, 'url', '');
        if (!isEmpty(url)) {
          gridExtraFilters.push(get(parentValue, 'url'));
        }
      }

      let filteredEventIds: string[];
      const eventIds = get(this.field, 'control.value', []);
      if (!isEmpty(eventIds)) {
        try {
          this.uiActions.enableLoader();
          filteredEventIds = await this.xprtDataService
            .getEventIdsByFilter({
              status: this.availableEventStatus,
              ids: eventIds,
              defaultFilter: [
                ...this.filterForPortalSelector,
                ...gridExtraFilters,
              ],
            })
            .toPromise();
          this.uiActions.disableLoader();
        } catch (error) {
          this.uiActions.disableLoader();
        }
      }

      result = await this.xprtDataActions.showEventSelector({
        gridExtraFilters: compact([
          ...gridExtraFilters,
          ...this.filterForPortalSelector,
          !isEmpty(this.availableEventStatus)
            ? `status eq in(${this.availableEventStatus.join(',')})`
            : '',
        ]),
        multiply: this.multiply,
        selectionLimit: this.maxItems,
        isSubmitEnabled: !this.minItems,
        defaultSelectedEvents: filteredEventIds || [],
        defaultSort: `${this.sortForPortalSelector.join(',')}`,
        select: this.selectForEventPicker,
      } as IEventPickerOptions);

      this.modalIsHidden$?.next(true);
      if (isEqual(result.modalResult, CANCEL_ACTION)) {
        return;
      }

      if (this.updateSelectOptionsWhenSingleSelected) {
        this.selectOptions$.next(result.items);
        if (isSubject(this.field.selectOptions)) {
          this.field.selectOptions.next(result.items);
        }
      }

      this.setValue(map(result.items, 'id'));

      return;
    }
    result = await this.openEventSelectorModal();
    if (!result.confirmed) return;
    const selectedEventId = result.value.events;
    this.setValue(selectedEventId);
  }

  public async openEventSelectorModal(): Promise<IModalDialogResult> {
    const events = await this.fetchEventBySelf();
    const eventSelectorState = {
      list: events,
      selectedEventIds: this.field.control.value || [],
    };
    this.selectOptions$.next(events);

    const modalOptions = eventSelectorModalDefinition.eventSelectorModal(
      eventSelectorState,
      this.availableEventStatus,
      this.multiply ? GRID_ROW_SELECTION.MULTIPLE : GRID_ROW_SELECTION.SINGLE,
      this.field,
    );
    const dialogResult = await this.uiActions.promptFormModal(modalOptions);

    return {
      confirmed: dialogResult.confirmed,
      modalResult: dialogResult.modalResult,
      value: dialogResult.value,
    };
  }

  protected setValue(value): void {
    this.field.control.setValue(value);
    this.field.control.markAsDirty();
  }

  protected async fetchEventBySelf(): Promise<IProcessedEvent[]> {
    this.uiActions.enableLoader();

    let events: IProcessedEvent[] = [];

    try {
      events = await this.xprtDataService
        .fetchCompleteEventsList({})
        .pipe(rx.first())
        .toPromise();
    } catch (error) {}

    this.uiActions.disableLoader();

    return events;
  }
}
